import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Spinner } from 'react-bootstrap';
import { Link, Navigate } from 'react-router-dom';

import Card from 'components/DesignSystem/Card';
import Button from 'components/DesignSystem/Button';
import Checkbox from 'components/DesignSystem/Checkbox';
import QueryErrorState from 'components/DesignSystem/QueryErrorState';
import SubtitleRegular from 'components/Typography/SubtitleRegular';
import BodyRegular from 'components/Typography/BodyRegular';
import Avatar from 'components/Relatorios/shared/Aprovacao/Avatar';
import SecaoStatusPill from 'components/Relatorios/shared/Aprovacao/SecaoStatusPill';
import SecoesListaMobile from 'components/Relatorios/shared/Aprovacao/PainelAprovacao/SecoesListaMobile';
import ProgressoAprovacao from 'components/Relatorios/shared/Aprovacao/ProgressoAprovacao';
import PublicarModal from 'components/Relatorios/shared/Aprovacao/PublicarModal';
import useRelatorioAprovacao from 'hooks/Relatorios/Aprovacao/useRelatorioAprovacao';
import useIsMobile from 'utils/hooks/useIsMobile';
import {
  corDoEvento, descreverEvento, formatarDataBR, formatarDataHora, labelDaVariante,
  labelDaSecao, rotuloDaDataDoInforme,
  varianteDoStatus,
} from 'helper/Relatorios/Aprovacao';

import './index.scss';

type Resumo = 'publicado' | 'completo' | 'revisao';

function resumoDoDia(publicado: boolean, aprovadas: number, total: number): Resumo {
  if (publicado) return 'publicado';
  if (aprovadas === total) return 'completo';
  return 'revisao';
}

const RESUMO_LABEL: Record<Resumo, string> = {
  publicado: 'PUBLICADO',
  completo: 'APROVADO — PRONTO PARA PUBLICAR',
  revisao: 'EM REVISÃO',
};

/**
 * Rota `<base>/aprovacao` do informe: card "Aprovação do informe" (tabela de
 * seções + ações em massa) e card "Histórico de atividade". Guarda com
 * `<Navigate>` se não estiver em `modoRevisao` (não-admin, flag off, ou dia
 * sem informe).
 */
export default function PainelAprovacao() {
  const {
    config, modoRevisao, params, status, isLoading, isError, refetch,
    aprovadas, total, percentual, podePublicar, publicado,
    statusPorSlug, aprovar, revogar, mutando, erroMutacao,
  } = useRelatorioAprovacao();
  const isMobile = useIsMobile();
  const [modalAberto, setModalAberto] = useState(false);
  // Estado de UI (não altera o DOM no desktop): seção da última ação individual, registrada ANTES
  // de disparar. Assim, quando o erro chega ao store, a mensagem (role="alert") já monta citando a
  // seção, em vez de montar genérica e trocar de texto quando o `.catch` rodasse (anúncio duplo).
  const [secaoDaAcao, setSecaoDaAcao] = useState<string | null>(null);
  const secaoComErro = erroMutacao !== undefined && !mutando ? secaoDaAcao : null;

  useEffect(() => {
    setSecaoDaAcao(null);
  }, [params]);

  const pendentesSlugs = useMemo(() => (
    config.secoes
      .filter(({ slug }) => statusPorSlug[slug]?.status === 'pendente')
      .map(({ slug }) => slug)
  ), [config.secoes, statusPorSlug]);

  const aprovadasSlugs = useMemo(() => (
    config.secoes
      .filter(({ slug }) => statusPorSlug[slug]?.status === 'aprovada')
      .map(({ slug }) => slug)
  ), [config.secoes, statusPorSlug]);

  const handleAprovarPendentes = useCallback(() => {
    setSecaoDaAcao(null);
    aprovar(pendentesSlugs).catch(() => undefined);
  }, [aprovar, pendentesSlugs]);

  const handleRevogarTodas = useCallback(() => {
    setSecaoDaAcao(null);
    revogar(aprovadasSlugs).catch(() => undefined);
  }, [revogar, aprovadasSlugs]);

  const handleToggleSecao = useCallback((slug: string) => (
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSecaoDaAcao(slug);
      const acao = event.target.checked ? aprovar([slug]) : revogar([slug]);
      // Sucesso: esquece a seção, para um erro antigo da outra mutação não passar a citá-la.
      acao.then(() => setSecaoDaAcao(null), () => undefined);
    }
  ), [aprovar, revogar]);

  if (!modoRevisao) return <Navigate to={`${config.base}/${config.secoes[0].slug}`} replace />;

  if (isLoading && !status) {
    return (
      <div className="d-flex justify-content-center p-3">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (isError) {
    return (
      <QueryErrorState
        title="Não foi possível carregar o painel de aprovação"
        onRetry={refetch}
      />
    );
  }

  if (!status || status.status === 'sem_informe') return <Navigate to={`${config.base}/${config.secoes[0].slug}`} replace />;

  const dataBR = rotuloDaDataDoInforme(config, status.data_rodada);
  const resumo = resumoDoDia(publicado, aprovadas, total);
  const badge = RESUMO_LABEL[resumo];

  return (
    <div className="relatorio-aprovacao-painel-aprovacao">
      <Card title="APROVAÇÃO DO INFORME" className="relatorio-aprovacao-painel-aprovacao__card">
        <BodyRegular>
          <span className="relatorio-aprovacao-painel-aprovacao__subtitulo">
            {`${config.titulo} · ${dataBR} · ${config.variante.rotulo} ${labelDaVariante(config, varianteDoStatus(config, status))} — a aprovação é feita por dia.`}
          </span>
        </BodyRegular>
        <div className="relatorio-aprovacao-painel-aprovacao__resumo">
          <span className={`relatorio-aprovacao-painel-aprovacao__badge relatorio-aprovacao-painel-aprovacao__badge--${resumo}`}>
            {badge}
          </span>
          <ProgressoAprovacao aprovadas={aprovadas} total={total} percentual={percentual} />
          {!publicado && (
            <Button
              type="secondary"
              className="relatorio-aprovacao-painel-aprovacao__aprovar-pendentes"
              small
              disabled={pendentesSlugs.length === 0 || mutando}
              onClick={handleAprovarPendentes}
            >
              Aprovar seções pendentes
            </Button>
          )}
        </div>

        {isMobile ? (
          <SecoesListaMobile
            secoes={config.secoes}
            statusPorSlug={statusPorSlug}
            base={config.base}
            publicado={publicado}
            mutando={mutando}
            secaoComErro={secaoComErro}
            onToggleSecao={handleToggleSecao}
          />
        ) : (
          <div className="table-responsive relatorio-aprovacao-painel-aprovacao__tabela-wrapper">
            <table className="relatorio-aprovacao-painel-aprovacao__tabela">
              <thead>
                <tr>
                  <th aria-label="Aprovada" className="relatorio-aprovacao-painel-aprovacao__col-check" />
                  <th>SEÇÃO</th>
                  <th>STATUS</th>
                  <th>RESPONSÁVEL / QUANDO</th>
                  <th className="relatorio-aprovacao-painel-aprovacao__col-acoes">AÇÕES</th>
                </tr>
              </thead>
              <tbody>
                {config.secoes.map(({ slug, label }) => {
                  const secao = statusPorSlug[slug];
                  if (!secao) return null;
                  const aprovada = secao.status === 'aprovada';
                  const autoria = secao.aprovacao ?? secao.ultima_edicao;
                  const prefixo = aprovada ? '' : 'Editado por ';

                  return (
                    <tr key={slug}>
                      <td className="relatorio-aprovacao-painel-aprovacao__col-check">
                        <Checkbox
                          className="relatorio-aprovacao-painel-aprovacao__checkbox"
                          checked={aprovada}
                          disabled={publicado || mutando}
                          onChange={handleToggleSecao(slug)}
                          aria-label={`${aprovada ? 'Revogar aprovação de' : 'Aprovar'} ${label}`}
                        />
                      </td>
                      <td>
                        <Link to={`${config.base}/${slug}`} className="relatorio-aprovacao-painel-aprovacao__secao">{label}</Link>
                      </td>
                      <td><SecaoStatusPill status={secao.status} /></td>
                      <td>
                        {autoria && (
                          <BodyRegular>
                            <span className="relatorio-aprovacao-painel-aprovacao__responsavel">
                              {`${prefixo}${autoria.por} · ${formatarDataHora(autoria.em)}`}
                            </span>
                          </BodyRegular>
                        )}
                      </td>
                      <td className="relatorio-aprovacao-painel-aprovacao__col-acoes">
                        <Link to={`${config.base}/${slug}`} className="relatorio-aprovacao-painel-aprovacao__revisar">Revisar</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {erroMutacao !== undefined && (
          <BodyRegular>
            <span
              className="relatorio-aprovacao-painel-aprovacao__erro"
              role={isMobile ? 'alert' : undefined}
            >
              {isMobile && secaoComErro !== null
                ? `Não foi possível concluir a ação em ${labelDaSecao(secaoComErro, config.secoes)}. Tente novamente.`
                : 'Não foi possível concluir a ação. Tente novamente.'}
            </span>
          </BodyRegular>
        )}

        <div className="relatorio-aprovacao-painel-aprovacao__rodape">
          {!publicado && (
            <>
              <Button
                className="relatorio-aprovacao-painel-aprovacao__publicar"
                disabled={!podePublicar}
                onClick={() => setModalAberto(true)}
              >
                {`Publicar dia ${formatarDataBR(status.data_rodada)}`}
              </Button>
              <BodyRegular>
                <span className="relatorio-aprovacao-painel-aprovacao__ajuda">
                  {podePublicar
                    ? 'Todas as seções aprovadas — pronto para publicar este dia.'
                    : `Aprove as ${total - aprovadas} seções restantes para liberar a publicação.`}
                </span>
              </BodyRegular>
              {aprovadas > 0 && (
                <button type="button" className="relatorio-aprovacao-painel-aprovacao__revogar-todas" onClick={handleRevogarTodas} disabled={mutando}>
                  Revogar todas as aprovações
                </button>
              )}
            </>
          )}
          {publicado && status.publicacao && (
            <BodyRegular>
              <span className="relatorio-aprovacao-painel-aprovacao__ajuda">
                {`Publicado por ${status.publicacao.por} em ${formatarDataHora(status.publicacao.em)}.`}
              </span>
            </BodyRegular>
          )}
        </div>
      </Card>

      <Card title="HISTÓRICO DE ATIVIDADE" className="relatorio-aprovacao-painel-aprovacao__historico">
        <ul className="relatorio-aprovacao-painel-aprovacao__lista-eventos">
          {status.historico.map((evento) => {
            const { autor, texto } = descreverEvento(
              evento,
              config,
              varianteDoStatus(config, status),
            );
            return (
              <li key={evento.id}>
                <Avatar nome={evento.autor} cor={corDoEvento(evento.tipo)} />
                <div className="relatorio-aprovacao-painel-aprovacao__evento">
                  <BodyRegular>
                    <strong>{autor}</strong>
                    {` ${texto}`}
                  </BodyRegular>
                  <SubtitleRegular>{formatarDataHora(evento.em)}</SubtitleRegular>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>

      <PublicarModal open={modalAberto} onClose={() => setModalAberto(false)} />
    </div>
  );
}
